import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";

import LoadingButton from "@mui/lab/LoadingButton";
import {
  FilePresent,
  RemoveCircleOutlineOutlined,
  UploadFile,
} from "@mui/icons-material";
import {
  useUploadProjectDocumentMutation,
  useGetProjectsQuery,
} from "../../services/project.service";
import { useFilePicker } from "use-file-picker";
import ProjectSelector from "./ProjectSelector";
import { toast } from "react-toastify";

import { useCompany } from "../../hooks/useCompany";
import { CompanyStatus } from "../../types/company";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalUploadDocumentsProps {
  open: boolean;
  onClose: () => void;
}

const ModalUploadDocuments = ({ open, onClose }: ModalUploadDocumentsProps) => {
  const {
    data: projects,
    isLoading,
    isFetching,
    refetch,
  } = useGetProjectsQuery(
    {
      page: 0,
      pageSize: 1000,
      statuses: "draft,in-progress,publish",
      load: "",
      sortField: "created_at",
      sortMode: "desc",
    },
    {
      skip: !open,
    }
  );
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [progress, setProgress] = React.useState<number>(0);
  const [waiting, setWaiting] = React.useState<boolean>(false);
  const [currentlyUploading, setCurrentlyUploading] = React.useState<number>(0);

  // UI Labels
  const uploadDocumentsDescriptionLabel = useUILabel("project.uploadDocumentsDescription");
  const workDocumentsLabel = useUILabel("project.workDocuments");
  const chooseFileLabel = useUILabel("project.chooseFile");
  const attachingFileToProjectLabel = useUILabel("project.attachingFileToProject");
  const uploadSuccessfulLabel = useUILabel("project.uploadSuccessful");
  const pleaseChooseFileLabel = useUILabel("project.pleaseChooseFile");
  const loadingWorkLabel = useUILabel("project.loadingWork");
  const cancelLabel = useUILabel("common.cancel");
  const uploadLabel = useUILabel("project.uploadDocuments");
  const [
    openFileSelector,
    { plainFiles, loading, errors: uploadErrors, clear },
  ] = useFilePicker({
    multiple: false,
    accept: [
      ".doc",
      ".docx",
      ".pdf",
      ".txt",
      ".jpeg",
      ".jpg",
      ".png",
      ".gif",
      ".pptx",
      ".zip",
      ".xls",
      "xlsx",
      ".csv",
    ],
    maxFileSize: 50,
    readFilesContent: false,
  });
  const company = useCompany();
  const [uploadDoc, { isLoading: uploading }] =
    useUploadProjectDocumentMutation();
  const [file, setFile] = React.useState<File | null>();

  const fileSizeTooSmallLabel = useUILabel("project.fileSizeTooSmall");
  const fileSizeTooLargeLabel = useUILabel("project.fileSizeTooLarge");
  const problemReadingFileLabel = useUILabel("project.problemReadingFile");
  const tooManyFilesLabel = useUILabel("project.tooManyFiles");
  const notEnoughFilesLabel = useUILabel("project.notEnoughFiles");

  React.useEffect(() => {
    uploadErrors?.[0]?.fileSizeTooSmall &&
      toast.error(fileSizeTooSmallLabel);
    uploadErrors?.[0]?.fileSizeToolarge &&
      toast.error(fileSizeTooLargeLabel);
    uploadErrors?.[0]?.readerError &&
      toast.error(problemReadingFileLabel);
    uploadErrors?.[0]?.maxLimitExceeded && toast.error(tooManyFilesLabel);
    uploadErrors?.[0]?.minLimitNotReached && toast.error(notEnoughFilesLabel);
  }, [uploadErrors, fileSizeTooSmallLabel, fileSizeTooLargeLabel, problemReadingFileLabel, tooManyFilesLabel, notEnoughFilesLabel]);

  function timeout(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function asyncUploadDoc() {
    setWaiting(true);
    if (file) {
      for (let i = 0; i < selectedIds.length; i++) {
        setCurrentlyUploading(selectedIds[i]);
        await timeout(500);
        await uploadDoc({
          projectId: selectedIds[i],
          file,
          refetch: false,
        }).unwrap();

        setProgress((i / selectedIds.length) * 100);
      }
    }
  }

  const onSubmit = () => {
    if (!file) {
      toast.error(pleaseChooseFileLabel);
      return;
    }
    asyncUploadDoc()
      .then((res) => {
        toast.success(uploadSuccessfulLabel);
        onClose();
      })
      .catch((err) => {
        toast.error(err.message);
        clear();
        onClose();
      })
      .finally(() => {
        setWaiting(false);
      });
  };

  React.useEffect(() => {
    if (plainFiles?.length > 0) {
      setFile(plainFiles[0]);
    }
  }, [plainFiles]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="md"
      style={{ overflowY: "hidden" }}
    >
      <DialogTitle id="alert-dialog-title">
        {uploadDocumentsDescriptionLabel}
      </DialogTitle>
      <Box sx={{ paddingRight: 3, paddingLeft: 3, paddingBottom: 3 }}>
        <Card>
          <CardHeader title={workDocumentsLabel} />
          <CardContent>
            {file && (
              <List>
                <ListItem
                  key={file.name}
                  secondaryAction={
                    <IconButton
                      onClick={() => {
                        setFile(null);
                      }}
                    >
                      <RemoveCircleOutlineOutlined
                        fontSize="small"
                        color="error"
                      />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <FilePresent />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${file.name || "n/a"}`}
                    primaryTypographyProps={{
                      fontWeight: "500",
                      fontSize: 16,
                    }}
                    // secondary={
                    //   <Link href={file} target="_blank">
                    //     View
                    //   </Link>
                    // }
                    sx={{ flex: 1 }}
                  />
                </ListItem>
              </List>
            )}
            <LoadingButton
              variant="contained"
              size="small"
              onClick={() => openFileSelector()}
              disabled={company?.status != CompanyStatus.ACTIVE}
              startIcon={<UploadFile />}
              sx={{ ml: 1 }}
              loading={uploading || waiting}
            >
              {chooseFileLabel}
            </LoadingButton>
            {waiting && (
              <div>
                <Box sx={{ ml: 1, mt: 2 }}>
                  {attachingFileToProjectLabel} {currentlyUploading}
                </Box>
                <LinearProgress
                  sx={{ ml: 1, mt: 1 }}
                  variant="determinate"
                  value={progress}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </Box>
      <Divider />
      <DialogContent>
        <Card sx={{ mt: 1 }}>
          {isLoading && (
            <Box sx={{ alignItems: "center", padding: 2 }}>
              <Typography variant="body1">{loadingWorkLabel}</Typography>
            </Box>
          )}
          <ProjectSelector
            selected={selectedIds}
            setSelected={setSelectedIds}
            projects={projects?.data || []}
          />
        </Card>
      </DialogContent>
      {/* <Box sx={{ paddingRight: 3, paddingLeft: 3, paddingBottom: 3 }}>
        <Card sx={{ mt: 1 }}>
          <Divider />
          <ProjectSelector
            selected={selectedIds}
            setSelected={setSelectedIds}
            projects={projects?.data}
          />
        </Card>
      </Box> */}
      <DialogActions>
        <Button onClick={onClose}>{cancelLabel}</Button>
        <LoadingButton
          variant="contained"
          loading={false}
          onClick={() => {
            onSubmit();
          }}
        >
          {uploadLabel}
          {/* {!data ? "Create" : "Update"} */}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
export default ModalUploadDocuments;
